/* eslint-disable @typescript-eslint/no-explicit-any */
import axios, {
  AxiosInstance,
  AxiosResponse,
  AxiosError,
  AxiosRequestConfig,
} from "axios";
import { logger } from "../utils/logger";
import { API_PATHS } from "../config/api.config";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3000/api";

export interface CustomAxiosRequestConfig extends AxiosRequestConfig {
  skipAuthRedirect?: boolean;
  /** Internal flag — marks this as the refresh attempt itself to avoid loops */
  _isRefreshCall?: boolean;
}

export interface ApiConfig {
  onSuccess?: (response: AxiosResponse) => void;
  onError?: (error: AxiosError) => void;
  onTokenExpiry?: () => void;
}

export class HttpClient {
  private instance: AxiosInstance;
  private config: ApiConfig;

  /** True while a token refresh is in flight */
  private isRefreshing = false;

  /**
   * Callbacks waiting for the refresh to complete.
   * Each is called with `true` (retry) or `false` (give up → logout).
   */
  private refreshQueue: Array<(ok: boolean) => void> = [];

  constructor(config: ApiConfig = {}) {
    this.config = config;
    this.instance = axios.create({
      baseURL: API_URL,
      withCredentials: true,
      timeout: 30000,
      headers: {
        "Content-Type": "application/json",
      },
    });

    this.initializeInterceptors();
  }

  private initializeInterceptors() {
    this.instance.interceptors.response.use(
      (response) => {
        const payload: any = response.data as any;
        if (
          payload &&
          typeof payload === "object" &&
          "success" in payload &&
          "data" in payload
        ) {
          response.data = payload.data;
        }
        if (this.config.onSuccess) {
          this.config.onSuccess(response);
        }
        return response;
      },
      (error: AxiosError) => {
        const reqConfig = error.config as CustomAxiosRequestConfig;

        if (
          error.code === "ECONNABORTED" ||
          error.message?.includes("timeout")
        ) {
          error.message = "Connection timed out. Please try again.";
        }

        if (error.response?.status === 401) {
          // Never retry the refresh call itself, and honour explicit opt-out
          if (reqConfig?._isRefreshCall || reqConfig?.skipAuthRedirect) {
            return Promise.reject(error);
          }

          // Queue this request and trigger a single refresh attempt
          return new Promise<AxiosResponse>((resolve, reject) => {
            this.refreshQueue.push((ok: boolean) => {
              if (ok) {
                resolve(this.instance.request(reqConfig));
              } else {
                reject(error);
              }
            });

            if (!this.isRefreshing) {
              this.isRefreshing = true;
              this.instance
                .post(
                  API_PATHS.AUTH.REFRESH,
                  {},
                  { _isRefreshCall: true } as CustomAxiosRequestConfig,
                )
                .then(() => {
                  this.isRefreshing = false;
                  this.flushQueue(true);
                })
                .catch(() => {
                  this.isRefreshing = false;
                  this.flushQueue(false);
                  if (this.config.onTokenExpiry) {
                    this.config.onTokenExpiry();
                  }
                });
            }
          });
        } else if (this.config.onError) {
          this.config.onError(error);
        }

        return Promise.reject(error);
      },
    );
  }

  private flushQueue(ok: boolean) {
    const queue = this.refreshQueue;
    this.refreshQueue = [];
    queue.forEach((cb) => cb(ok));
  }

  public async request<T = any>(
    config: CustomAxiosRequestConfig,
  ): Promise<AxiosResponse<T>> {
    return this.instance.request<T>(config);
  }
}

/**
 * Swappable logout handler — defaults to a plain redirect.
 * AuthContext replaces this with its full logout() once mounted
 * so that cookie clearing and server-side session invalidation happen properly.
 */
let _logoutHandler: () => void = () => {
  window.location.href = "/login";
};

export const setLogoutHandler = (fn: () => void) => {
  _logoutHandler = fn;
};

const httpClient = new HttpClient({
  onTokenExpiry: () => {
    _logoutHandler();
  },
  onError: (error) => {
    logger.error("API Error:", error);
  },
});

export const api = {
  request: <T = any>(config: CustomAxiosRequestConfig) =>
    httpClient.request<T>(config),
};

export default httpClient;
